import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Clock,
  Calendar,
  User,
  Trophy,
  Target,
  BarChart3,
  LayoutGrid,
  List,
} from "lucide-react";
import { resolveMediaUrl } from "@/lib/resolveMediaUrl";
import type { TReportItem } from "@/types/reports";

function formatDate(date: Date, style: "short" | "long"): string {
  if (style === "long") {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

interface CorporateReportsListProps {
  reportsData: TReportItem[];
}

function ScenarioImage({
  src,
  alt,
  className,
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  const [error, setError] = useState(false);
  if (error || !src) {
    return (
      <div
        className={`w-full h-full ${className ?? ""}`}
        style={{ background: "var(--muted)" }}
      >
        <div className="w-full h-full flex items-center justify-center">
          <Target className="h-12 w-12 text-muted-foreground/20" />
        </div>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setError(true)}
    />
  );
}

export function CorporateReportsList({ reportsData }: CorporateReportsListProps) {
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");

  if (!reportsData || reportsData.length === 0) {
    return (
      <Card className="p-12 text-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
            <Trophy className="h-8 w-8 text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-2">No Reports Found</h3>
            <p className="text-sm text-muted-foreground">
              This user hasn&apos;t completed any scenarios yet.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const getSeverityColor = (severity?: string) => {
    switch (severity?.toLowerCase()) {
      case "very easy":
        return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400";
      case "easy":
        return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400";
      case "medium":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400";
      case "hard":
        return "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400";
      case "very hard":
        return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400";
    }
  };

  const getScorePercentage = (
    obtained?: string | number,
    total?: string | number
  ) => {
    const obtainedNum = Number(obtained || 0);
    const totalNum = Number(total || 1);
    return totalNum > 0 ? (obtainedNum / totalNum) * 100 : 0;
  };

  const renderGridCard = (report: TReportItem) => {
    const scenario = report.scenario;
    const scorePercentage = getScorePercentage(
      report.total_obtained_score,
      report.total_score
    );
    const isPerfectScore = scorePercentage === 100;

    return (
      <Card
        key={report.id}
        className="group overflow-hidden hover:shadow-xl transition-all duration-300 border border-border bg-card hover:border-primary/50"
      >
        <CardContent className="p-0">
          <div className="relative w-full h-48 bg-gradient-to-br from-muted/30 to-muted/60 overflow-hidden">
            {scenario?.thumbnail_url ? (
              <>
                <ScenarioImage
                  src={resolveMediaUrl(scenario.thumbnail_url) || scenario.thumbnail_url}
                  alt={scenario?.name || "Scenario"}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Target className="h-12 w-12 text-muted-foreground/20" />
              </div>
            )}
            <div className="absolute top-3 right-3">
              <div
                className={`rounded-xl px-3 py-1.5 backdrop-blur-md shadow-lg border border-white/20 ${
                  isPerfectScore
                    ? "bg-emerald-500/90 text-white"
                    : scorePercentage >= 50
                      ? "bg-blue-500/90 text-white"
                      : "bg-orange-500/90 text-white"
                }`}
              >
                <div className="flex items-center gap-1">
                  <Trophy className="h-3.5 w-3.5" />
                  <span className="text-xs font-bold">
                    {Math.round(scorePercentage)}%
                  </span>
                </div>
              </div>
            </div>
            {scenario?.type && (
              <div className="absolute bottom-3 left-3">
                <Badge className="bg-white/90 text-gray-900 dark:bg-white/90 dark:text-gray-900 backdrop-blur-sm shadow-md text-xs">
                  {scenario.type}
                </Badge>
              </div>
            )}
          </div>
          <div className="p-5">
            <h3 className="text-lg font-bold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors min-h-[56px]">
              {scenario?.name || "Unnamed Scenario"}
            </h3>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {scenario?.category_name && (
                <Badge variant="outline" className="text-xs px-2 py-0.5">
                  {scenario.category_name}
                </Badge>
              )}
              {scenario?.severity && (
                <Badge
                  className={`text-xs px-2 py-0.5 ${getSeverityColor(scenario.severity)}`}
                >
                  {scenario.severity}
                </Badge>
              )}
            </div>
            <div className="space-y-2 mb-4 text-xs text-muted-foreground">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <Trophy className="h-3 w-3 text-amber-500" />
                  Points
                </span>
                <span className="font-semibold text-foreground">
                  {scenario?.points ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3 text-blue-500" />
                  Started
                </span>
                <span className="font-medium text-foreground">
                  {report.start_time
                    ? formatDate(new Date(report.start_time), "long")
                    : "N/A"}
                </span>
              </div>
              {scenario?.started_by?.user_full_name && (
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3 text-purple-500" />
                    By
                  </span>
                  <span className="font-medium text-foreground truncate ml-2">
                    {scenario.started_by.user_full_name}
                  </span>
                </div>
              )}
            </div>
            <div className="bg-muted/30 rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground font-medium">
                  Score
                </span>
                <span className="text-lg font-bold text-foreground">
                  {report.total_obtained_score ?? 0}
                  <span className="text-sm text-muted-foreground">
                    /{report.total_score ?? 0}
                  </span>
                </span>
              </div>
              <Progress value={scorePercentage} className="h-2" />
              <p className="text-xs text-right mt-1 font-medium text-muted-foreground">
                {Math.round(scorePercentage)}% Achievement
              </p>
            </div>
            <Button size="sm" asChild className="w-full gap-2">
              <Link to={`/report/${report.id}`}>
                <BarChart3 className="h-4 w-4" />
                View Report
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderListCard = (report: TReportItem) => {
    const scenario = report.scenario;
    const scorePercentage = getScorePercentage(
      report.total_obtained_score,
      report.total_score
    );
    const isPerfectScore = scorePercentage === 100;

    return (
      <Card
        key={report.id}
        className="group overflow-hidden hover:shadow-lg transition-all duration-300 border border-border bg-card hover:border-primary/50"
      >
        <CardContent className="p-0">
          <div className="flex flex-col sm:flex-row">
            <div className="relative w-full sm:w-48 h-32 sm:h-auto bg-gradient-to-br from-muted/30 to-muted/60 flex-shrink-0 overflow-hidden">
              {scenario?.thumbnail_url ? (
                <>
                  <ScenarioImage
                    src={resolveMediaUrl(scenario.thumbnail_url) || scenario.thumbnail_url}
                    alt={scenario?.name || "Scenario"}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Target className="h-10 w-10 text-muted-foreground/20" />
                </div>
              )}
              <div className="absolute top-2 right-2">
                <div
                  className={`rounded-lg px-2.5 py-1 backdrop-blur-sm shadow-lg ${
                    isPerfectScore
                      ? "bg-emerald-500/90 text-white"
                      : scorePercentage >= 50
                        ? "bg-blue-500/90 text-white"
                        : "bg-orange-500/90 text-white"
                  }`}
                >
                  <span className="text-xs font-bold">
                    {Math.round(scorePercentage)}%
                  </span>
                </div>
              </div>
            </div>
            <div className="flex-1 p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-foreground mb-2 line-clamp-1 group-hover:text-primary transition-colors">
                    {scenario?.name || "Unnamed Scenario"}
                  </h3>
                  <div className="flex flex-wrap items-center gap-1.5 mb-3">
                    {scenario?.category_name && (
                      <Badge variant="outline" className="text-xs px-2 py-0.5">
                        {scenario.category_name}
                      </Badge>
                    )}
                    {scenario?.severity && (
                      <Badge
                        className={`text-xs px-2 py-0.5 ${getSeverityColor(scenario.severity)}`}
                      >
                        {scenario.severity}
                      </Badge>
                    )}
                    {scenario?.type && (
                      <Badge variant="secondary" className="text-xs px-2 py-0.5">
                        {scenario.type}
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Trophy className="h-3 w-3 text-amber-500" />
                      <span className="font-medium text-foreground">
                        {scenario?.points ?? 0}
                      </span>{" "}
                      pts
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-blue-500" />
                      {report.start_time
                        ? formatDate(new Date(report.start_time), "short")
                        : "N/A"}
                    </span>
                    {report.end_time && (
                      <>
                        <span>→</span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-emerald-500" />
                          {formatDate(new Date(report.end_time), "short")}
                        </span>
                      </>
                    )}
                    {scenario?.started_by?.user_full_name && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-1 truncate max-w-[150px]">
                          <User className="h-3 w-3 text-purple-500 flex-shrink-0" />
                          {scenario.started_by.user_full_name}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex sm:flex-col items-center sm:items-end gap-3 sm:gap-2">
                  <div className="flex items-center gap-2 sm:flex-col sm:items-end sm:gap-1">
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Score</p>
                      <p className="text-xl font-bold text-foreground">
                        {report.total_obtained_score ?? 0}
                        <span className="text-sm text-muted-foreground">
                          /{report.total_score ?? 0}
                        </span>
                      </p>
                    </div>
                    <div className="w-24 sm:w-32">
                      <Progress value={scorePercentage} className="h-1.5" />
                    </div>
                  </div>
                  <Button size="sm" asChild className="gap-1.5 whitespace-nowrap">
                    <Link to={`/report/${report.id}`}>
                      <Trophy className="h-3.5 w-3.5" />
                      View Report
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <div className="inline-flex rounded-lg border border-border bg-muted p-1">
          <Button
            variant={viewMode === "list" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setViewMode("list")}
            className="gap-2 h-8"
          >
            <List className="h-4 w-4" />
            <span className="hidden sm:inline">List</span>
          </Button>
          <Button
            variant={viewMode === "grid" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setViewMode("grid")}
            className="gap-2 h-8"
          >
            <LayoutGrid className="h-4 w-4" />
            <span className="hidden sm:inline">Grid</span>
          </Button>
        </div>
      </div>
      {viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reportsData.map(renderGridCard)}
        </div>
      ) : (
        <div className="space-y-3">
          {reportsData.map(renderListCard)}
        </div>
      )}
    </div>
  );
}
